import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, RefreshCw, Sparkles, Briefcase, TrendingUp,
  AlertTriangle, Award, ArrowRight, Route, Users, Plane, Zap, Target,
} from "lucide-react";
import { api } from "@/lib/api";

const fmtCad = (v) => v == null ? "—" : new Intl.NumberFormat("fr-CA",
  { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(v);


export default function DossierCareers() {
  const { dossier_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/careers/for-dossier/${dossier_id}/latest`);
      setData(d.empty ? null : d);
    } catch { toast.error("Chargement impossible."); }
    finally { setLoading(false); }
  };

  const discover = async () => {
    setRunning(true);
    try {
      const { data: d } = await api.post(`/careers/for-dossier/${dossier_id}/discover?force=true`);
      setData(d);
      toast.success("Découverte carrière générée.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossible de lancer la découverte.");
    } finally { setRunning(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dossier_id]);

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="dossier-careers-page">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <Link to={`/app/dossiers/${dossier_id}`}
                  data-testid="careers-back-to-dossier"
                  className="text-xs text-[#5B5B62] hover:text-[#0B2545] inline-flex items-center gap-1.5">
              <ArrowLeft className="h-3.5 w-3.5" /> Retour au dossier
            </Link>
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14] mt-3">
              Career Discovery Engine
            </div>
            <h1 className="mt-1 font-display text-3xl tracking-tight text-[#1F2937]">
              Opportunités cachées de carrière
            </h1>
            <p className="mt-2 text-sm text-[#5B5B62] max-w-2xl">
              À partir du profil du client, ALTIORA identifie les postes cibles, écarts salariaux,
              pénuries sectorielles, reconversions rentables, certifications catalyseurs et voies
              d'immigration qui améliorent la trajectoire économique.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={discover} disabled={running}
                    data-testid="careers-discover-button"
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-4 py-2 text-sm font-medium">
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {running ? "Analyse…" : (data ? "Recalculer" : "Lancer la découverte")}
            </button>
          </div>
        </div>

        {loading && (
          <div className="p-12 flex items-center justify-center text-[#5B5B62]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
          </div>
        )}

        {!loading && !data && (
          <div className="rounded-2xl border-2 border-dashed border-[#C5A880]/60 bg-white/70 p-10 text-center"
               data-testid="careers-empty">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#FBF6EA] flex items-center justify-center">
              <Briefcase className="h-7 w-7 text-[#7A5B14]" strokeWidth={1.5} />
            </div>
            <h3 className="mt-5 font-display text-2xl text-[#1F2937] tracking-tight">
              Aucune découverte lancée
            </h3>
            <p className="mt-2 text-sm text-[#5B5B62] max-w-lg mx-auto">
              Cliquez « Lancer la découverte » — ALTIORA analyse le profil, le titre actuel, le secteur,
              la province et les revenus pour identifier les opportunités carrière cachées.
            </p>
          </div>
        )}

        {!loading && data && <DiscoveryContent data={data} />}
      </div>
    </div>
  );
}


function DiscoveryContent({ data }) {
  const cp = data.current_profession;
  const ins = data.insights || {};
  const ai = data.ai || {};

  return (
    <div className="space-y-6">
      {/* Current profession */}
      {cp && (
        <div className="rounded-lg border-2 border-[#0B2545]/25 bg-white p-5" data-testid="careers-current-profession">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">Profession actuelle détectée</div>
              <div className="font-display text-xl text-[#0B2545] mt-1">{cp.title_fr}</div>
              <div className="text-xs text-[#5B5B62]">CNP {cp.cnp_code} · secteur {cp.sector}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Médiane marché</div>
              <div className="font-mono text-2xl text-[#0B2545] tabular-nums">{fmtCad(cp.median_salary_cad)}</div>
            </div>
          </div>
        </div>
      )}

      {/* AI narrative */}
      {ai.narrative_fr && (
        <div className="rounded-lg border-2 border-[#C5A880]/40 bg-[#FBF6EA]/40 p-5" data-testid="careers-ai-narrative">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-[#7A5B14]" strokeWidth={2} />
            <h3 className="font-display text-base text-[#5A4308]">Lecture ALTIORA</h3>
            {ai.confidence_level && (
              <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
                Confiance {ai.confidence_level}
              </span>
            )}
          </div>
          <p className="text-sm text-[#1F2937] leading-relaxed">{ai.narrative_fr}</p>
          {ai.priority_actions_fr?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#C5A880]/40">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] mb-2">Priorités ce mois-ci</div>
              <ol className="space-y-1.5 text-sm text-[#1F2937]">
                {ai.priority_actions_fr.map((a, i) => (
                  <li key={i} className="flex gap-2"><span className="font-mono text-[10px] text-[#7A5B14] mt-1">{i + 1}.</span><span>{a}</span></li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* 6 insight cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Underpaid signal */}
        {ins.underpaid_signal && (
          <InsightCard tone="amber" icon={AlertTriangle} title="Signal sous-payé"
                       testid="careers-underpaid">
            <p className="text-sm text-[#78350F] leading-relaxed">{ins.underpaid_signal.hypothesis_fr}</p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <MiniStat label="Actuel" value={fmtCad(ins.underpaid_signal.current_income_cad)} />
              <MiniStat label="Médiane" value={fmtCad(ins.underpaid_signal.median_salary_cad)} />
              <MiniStat label="Écart" value={`+${fmtCad(ins.underpaid_signal.gap_cad)}`} tone="emerald" />
            </div>
          </InsightCard>
        )}

        {/* Shortage signal */}
        {ins.shortage_signal && (
          <InsightCard tone="emerald" icon={Zap} title="Pénurie de main-d'œuvre" testid="careers-shortage">
            <p className="text-sm text-emerald-900 leading-relaxed">
              <strong>{ins.shortage_signal.profession_title_fr}</strong> en pénurie officielle{" "}
              en <strong>{ins.shortage_signal.province}</strong>.
            </p>
            <p className="text-xs text-emerald-800 leading-relaxed mt-2">{ins.shortage_signal.impact_fr}</p>
          </InsightCard>
        )}

        {/* Target roles (career ladder) */}
        {ins.target_roles?.length > 0 && (
          <InsightCard tone="navy" icon={TrendingUp} title="Postes cibles" wide testid="careers-target-roles">
            <ul className="space-y-2">
              {ins.target_roles.map((r, i) => (
                <li key={i} className="flex items-baseline justify-between border-b border-[#E4E1D8] pb-2 last:border-b-0"
                    data-testid={`careers-target-role-${i}`}>
                  <div>
                    <div className="text-sm font-medium text-[#0B2545]">{r.title_fr}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                      {r.typical_years_to_reach ? `~ ${r.typical_years_to_reach} ans` : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm text-[#0B2545] tabular-nums">{fmtCad(r.salary_median_cad)}</div>
                    {r.salary_gap_cad != null && (
                      <div className="text-[10px] font-mono text-emerald-700">+{fmtCad(r.salary_gap_cad)} vs. actuel</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </InsightCard>
        )}

        {/* Reconversions */}
        {ins.reconversion_paths?.length > 0 && (
          <InsightCard tone="gold" icon={Route} title="Reconversions rentables" wide testid="careers-reconversion">
            <ul className="space-y-2">
              {ins.reconversion_paths.map((r, i) => (
                <li key={i} className="flex items-center justify-between border-b border-[#E4E1D8] pb-2 last:border-b-0"
                    data-testid={`careers-reconversion-${i}`}>
                  <div>
                    <div className="text-sm font-medium text-[#7A5B14]">{r.title_fr}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                      CNP {r.cnp_code} · {r.sector}
                      {r.shortage_here && <span className="ml-1 text-emerald-700">· pénurie ici</span>}
                    </div>
                  </div>
                  <div className="font-mono text-sm text-emerald-700 tabular-nums">
                    +{fmtCad(r.salary_uplift_cad)}
                  </div>
                </li>
              ))}
            </ul>
          </InsightCard>
        )}

        {/* Certifications */}
        {ins.certifications?.length > 0 && (
          <InsightCard tone="blue" icon={Award} title="Certifications catalyseurs" wide testid="careers-certifications">
            <ul className="space-y-2">
              {ins.certifications.map((c, i) => (
                <li key={i} className="flex items-center justify-between border-b border-[#E4E1D8] pb-2 last:border-b-0"
                    data-testid={`careers-certif-${i}`}>
                  <div className="text-sm font-medium text-blue-900">{c.name}</div>
                  <div className="font-mono text-sm text-emerald-700 tabular-nums">
                    +{fmtCad(c.salary_bump_cad)} typique
                  </div>
                </li>
              ))}
            </ul>
          </InsightCard>
        )}

        {/* Immigration */}
        {ins.immigration_by_profession?.length > 0 && (
          <InsightCard tone="navy" icon={Plane} title="Voies d'immigration favorisées" wide testid="careers-immigration">
            <ul className="space-y-2">
              {ins.immigration_by_profession.map((im, i) => (
                <li key={i} className="border-l-4 border-[#0B2545]/50 pl-3 py-1"
                    data-testid={`careers-immigration-${i}`}>
                  <div className="text-sm font-medium text-[#0B2545]">{im.label_fr}</div>
                  <div className="text-xs text-[#5B5B62] leading-relaxed">{im.why_fr}</div>
                </li>
              ))}
            </ul>
          </InsightCard>
        )}

        {/* Target employers hint */}
        {ins.target_employers_hint && (
          <InsightCard tone="grey" icon={Users} title="Employeurs cibles — méthode" wide testid="careers-employers-hint">
            <p className="text-sm text-[#5B5B62] leading-relaxed">{ins.target_employers_hint.hint_fr}</p>
            <div className="mt-2 text-xs text-[#7A5B14]">
              Secteur : <strong>{ins.target_employers_hint.sector}</strong>
              {ins.target_employers_hint.province && <> · Province : <strong>{ins.target_employers_hint.province}</strong></>}
            </div>
          </InsightCard>
        )}
      </div>

      {ai.risk_flags_fr?.length > 0 && (
        <div className="rounded-lg border-2 border-red-300 bg-red-50/40 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-800" strokeWidth={2} />
            <h3 className="font-display text-sm text-red-900">Points de vigilance</h3>
          </div>
          <ul className="text-sm text-red-900 space-y-1">
            {ai.risk_flags_fr.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        </div>
      )}

      <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] text-center">
        {data.method_version} · généré {new Date(data.generated_at || data.cached_at).toLocaleString("fr-CA")}
      </div>
    </div>
  );
}


function InsightCard({ tone = "navy", icon: Icon, title, children, wide, testid }) {
  const toneMap = {
    navy:    "border-[#0B2545]/25 bg-white",
    gold:    "border-[#C5A880] bg-white",
    amber:   "border-amber-300 bg-amber-50/40",
    emerald: "border-emerald-300 bg-emerald-50/40",
    blue:    "border-blue-300 bg-white",
    grey:    "border-[#E5E7EB] bg-white",
  };
  return (
    <div className={`border-2 rounded-lg p-5 ${toneMap[tone]} ${wide ? "md:col-span-2" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="h-4 w-4" strokeWidth={2} />}
        <h3 className="font-display text-base">{title}</h3>
      </div>
      {children}
    </div>
  );
}


function MiniStat({ label, value, tone }) {
  return (
    <div className={`rounded p-2 ${tone === "emerald" ? "bg-emerald-100" : "bg-white/50"}`}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#5B5B62]">{label}</div>
      <div className={`font-mono text-sm tabular-nums ${tone === "emerald" ? "text-emerald-800 font-bold" : "text-[#0B2545]"}`}>{value}</div>
    </div>
  );
}
